import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import { fgPortalMount } from "../lib/fgPortalMount";
import { focusFirstIn, useFocusTrap } from "../lib/useFocusTrap";

/**
 * Modal portal stack. Screens call `useModal().open(<Modal />)` to push a
 * modal on top of the planner / fuel-plan view; the modal closes itself via
 * `useModal().close()` or by clicking the scrim. Stack-based so the Customize
 * modal can be opened from inside the Fueling Plan and still return underneath
 * a still-open Preferences modal.
 *
 * **Closing:**
 * - Prefer `const id = open(<Modal onClose={() => close(id)} />)` so the right
 *   stack entry pops even under nested dialogs.
 * - `close()` alone pops the top entry.
 * - `ModalShell`/`button` callers often pass handlers as `onClick={onClose}`.
 *   If `onClose` were the raw `close` function from this context, React would
 *   invoke `close(syntheticEvent)` — we therefore treat non-string IDs as “pop top”
 *   so stray events never break dismissal.
 *
 * **Shopify shadow embed:** The dialog wrapper is a real box (not `display:contents`)
 * so hit-testing reliably reaches modal chrome inside the portal.
 *
 * Each entry's content is responsible for its own chrome (matches the design's
 * `ModalShell` pattern from `screens-modals-v3.jsx`). The provider only
 * supplies the scrim + portal mount.
 */
type ModalEntry = { id: string; node: ReactNode; onClose?: () => void };

type ContextValue = {
  open: (node: ReactNode, opts?: { id?: string; onClose?: () => void }) => string;
  close: (id?: string) => void;
  closeAll: () => void;
  /**
   * Whether any modal is currently open. A stable accessor (reads a ref) so
   * callers — e.g. global keyboard handlers that must stand down while a dialog
   * is up — can check at event time without subscribing to stack changes.
   */
  isOpen: () => boolean;
};

const ModalContext = createContext<ContextValue | null>(null);

let counter = 0;

export function ModalProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<ModalEntry[]>([]);
  // Mirror of the stack depth for the stable `isOpen` accessor.
  const openCountRef = useRef(0);
  openCountRef.current = stack.length;
  const isOpen = useCallback(() => openCountRef.current > 0, []);

  const close = useCallback((id?: string) => {
    setStack((curr) => {
      /** React invokes `close` with a click event when passed straight to `onClick={close}`. */
      const modalId = typeof id === "string" && id.length > 0 ? id : undefined;
      const target = modalId ? curr.find((m) => m.id === modalId) : curr[curr.length - 1];
      if (target?.onClose) target.onClose();
      return modalId ? curr.filter((m) => m.id !== modalId) : curr.slice(0, -1);
    });
  }, []);

  const open = useCallback<ContextValue["open"]>((node, opts) => {
    const id = opts?.id ?? `m-${++counter}`;
    setStack((curr) => [...curr, { id, node, onClose: opts?.onClose }]);
    return id;
  }, []);

  const closeAll = useCallback(() => {
    setStack((curr) => {
      for (const m of curr) m.onClose?.();
      return [];
    });
  }, []);

  // Close the top-of-stack modal on Escape.
  useEffect(() => {
    if (stack.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [stack.length, close]);

  // Lock body scroll while any modal is open.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (stack.length > 0) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [stack.length]);

  const value = useMemo<ContextValue>(
    () => ({ open, close, closeAll, isOpen }),
    [open, close, closeAll, isOpen]
  );

  return (
    <ModalContext.Provider value={value}>
      {children}
      {stack.length > 0 && typeof document !== "undefined" &&
        createPortal(
          <div className="fg-modal-stack">
            {stack.map((m, i) => (
              <ModalLayer
                key={m.id}
                entry={m}
                index={i}
                isTop={i === stack.length - 1}
                onScrimClose={() => close(m.id)}
              />
            ))}
          </div>,
          fgPortalMount() ?? document.body
        )}
    </ModalContext.Provider>
  );
}

/**
 * One entry in the modal stack: scrim + accessible dialog wrapper. Rendered as
 * its own component so each entry can own a ref and run the focus-trap hook.
 * The `role="dialog"` / `aria-modal` live on the dialog content wrapper (not
 * the scrim). Only the topmost entry traps Tab; lower entries are inert.
 *
 * `ModalShell` tags its `<h2>` with `data-fg-modal-title`; we copy a
 * per-entry unique id onto it so `aria-labelledby` resolves without duplicate
 * ids across stacked dialogs. Modals that don't use `ModalShell` fall back to
 * a generic `aria-label`.
 */
function ModalLayer({
  entry,
  index,
  isTop,
  onScrimClose
}: {
  entry: ModalEntry;
  index: number;
  isTop: boolean;
  onScrimClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, true, isTop);

  // If the modal content rendered a tagged title element, give it a per-entry
  // unique id and point aria-labelledby at it; otherwise fall back to aria-label.
  //
  // Re-synced on EVERY content change (MutationObserver), not just on mount: a
  // multi-step modal (the training-plan wizard) swaps its heading and the
  // focused control on each step, which would otherwise leave
  // `aria-labelledby` pointing at a removed element and drop focus onto
  // `document.body` — outside the Tab trap, so keyboard users could reach the
  // page (storefront) behind the dialog.
  const titleId = `fg-modal-title-${entry.id}`;
  const [hasTitle, setHasTitle] = useState(false);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const sync = () => {
      const el = dialog.querySelector<HTMLElement>("[data-fg-modal-title]");
      if (el) {
        if (el.id !== titleId) el.id = titleId;
        setHasTitle(true);
      } else {
        setHasTitle(false);
      }
      // Focus was on an element the step change removed → it fell to <body>.
      // Bring it back inside the top dialog (first control, else the dialog).
      if (isTop && document.activeElement === document.body) focusFirstIn(dialog);
    };
    sync();
    if (typeof MutationObserver === "undefined") return;
    let raf = 0;
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(sync);
    });
    observer.observe(dialog, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [titleId, isTop]);

  return (
    <div
      className="fg-modal-scrim"
      onClick={(e) => {
        // Only close when the scrim itself is clicked, not the modal body.
        if (e.target === e.currentTarget) onScrimClose();
      }}
      style={{ zIndex: `calc(var(--fg-z-scrim) + ${index})` }}>
      {/*
        Dialog shell is a real layout box (`fg-modal-dialog-mount`) so clicks and
        `:focus-visible` reliably hit the modal inside the Shopify shadow embed.
        Previously `display: contents` flattened children into the scrim grid, which
        broke hit-testing in some WebKit builds.
      */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-labelledby={hasTitle ? titleId : undefined}
        aria-label={hasTitle ? undefined : "Dialog"}
        className="fg-modal-dialog-mount">
        {entry.node}
      </div>
    </div>
  );
}

export function useModal(): ContextValue {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error("useModal must be called inside <ModalProvider>");
  return ctx;
}

/**
 * Build a sub-modal's "← Back" handler: close this modal, then run `onBack`
 * (which re-opens the hub). Returns `undefined` when there's no `onBack`, so
 * `ModalShell` hides the back chevron. Centralises the
 * `onBack ? () => { close(id); onBack(); } : undefined` pattern repeated across
 * every planner modal opener.
 *
 * `close` is a pre-bound closer (`() => closeModal(id)`) rather than an id, so
 * it can be used in the self-referential opener pattern where `id` isn't
 * assigned until `openModal` returns — the closure reads `id` lazily.
 */
export function modalBack(close: () => void, onBack?: () => void): (() => void) | undefined {
  return onBack
    ? () => {
        close();
        onBack();
      }
    : undefined;
}
